import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { Star, Quote, User, MessageSquare, ThumbsUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Review {
  id: string;
  client_name: string;
  rating: number;
  comment: string;
  created_at: string;
  professional: {
    name: string;
  };
}

interface Professional {
  id: string;
  name: string;
}

const Reviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    professional_id: '',
    rating: 0,
    comment: ''
  });

  useEffect(() => {
    fetchReviews();
    fetchProfessionals();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews_2025_11_26_20_41')
        .select(`
          *,
          professional:professionals_2025_11_26_20_41(name)
        `)
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Erro ao buscar avaliações:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfessionals = async () => {
    try {
      const { data, error } = await supabase
        .from('professionals_2025_11_26_20_41')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setProfessionals(data || []);
    } catch (error) {
      console.error('Erro ao buscar profissionais:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.professional_id || 
        formData.rating === 0 || !formData.comment) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('reviews_2025_11_26_20_41')
        .insert([formData]);

      if (error) throw error;

      toast({
        title: "Avaliação enviada!",
        description: "Sua avaliação foi enviada e será analisada antes da publicação.",
      });

      // Reset form
      setFormData({
        client_name: '',
        professional_id: '',
        rating: 0,
        comment: ''
      });
      setIsDialogOpen(false);

    } catch (error: any) {
      toast({
        title: "Erro ao enviar avaliação",
        description: error.message || "Ocorreu um erro ao enviar sua avaliação.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating: number, interactive = false, onRatingChange?: (rating: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${
              star <= rating 
                ? 'text-yellow-400 fill-current' 
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
            onClick={interactive && onRatingChange ? () => onRatingChange(star) : undefined}
          />
        ))}
      </div>
    );
  };

  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
    : 0;

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-12 bg-gray-200 rounded w-96 mx-auto mb-8"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            Avaliações
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            O que nossos
            <span className="block text-primary">pacientes dizem</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A satisfação dos nossos pacientes é nossa maior recompensa. 
            Veja o que eles têm a dizer sobre nossos serviços.
          </p>
        </div>

        {/* Estatísticas de avaliação */}
        <div className="bg-gray-50 rounded-2xl p-8 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary mb-2">
                {averageRating.toFixed(1)}
              </div>
              <div className="flex justify-center mb-2">
                {renderStars(Math.round(averageRating))}
              </div>
              <div className="text-gray-600">Avaliação Média</div>
            </div>
            
            <div>
              <div className="text-4xl font-bold text-secondary mb-2">
                {reviews.length}
              </div>
              <div className="text-gray-600">Avaliações</div>
            </div>
            
            <div>
              <div className="text-4xl font-bold text-accent mb-2">98%</div>
              <div className="text-gray-600">Satisfação</div>
            </div>
            
            <div>
              <div className="text-4xl font-bold text-primary mb-2">95%</div>
              <div className="text-gray-600">Recomendação</div>
            </div>
          </div>
        </div>

        {/* Grid de avaliações */}
        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {reviews.map((review) => (
              <Card key={review.id} className="hover:shadow-medium transition-smooth">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold">
                        {review.client_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {review.client_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {review.professional?.name}
                        </div>
                      </div>
                    </div>
                    <Quote className="h-8 w-8 text-primary/20" />
                  </div>

                  <div className="mb-4">
                    {renderStars(review.rating)}
                  </div>

                  <p className="text-gray-600 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>

                  <div className="text-sm text-gray-400">
                    {new Date(review.created_at).toLocaleDateString('pt-BR')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 mb-12">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Seja o primeiro a avaliar!
            </h3>
            <p className="text-gray-600">
              Compartilhe sua experiência conosco e ajude outros pacientes.
            </p>
          </div>
        )}

        {/* Call to action para deixar avaliação */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Teve uma boa experiência conosco?
          </h3>
          <p className="text-gray-600 mb-6">
            Sua opinião é muito importante para nós e para outros pacientes.
          </p>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gradient-primary text-white">
                <ThumbsUp className="mr-2 h-4 w-4" />
                Deixar Avaliação
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Deixe sua Avaliação</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="client_name">Seu Nome *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="Seu nome"
                    required
                  />
                </div>

                <div>
                  <Label>Profissional *</Label>
                  <Select 
                    value={formData.professional_id} 
                    onValueChange={(value) => setFormData({...formData, professional_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o profissional" />
                    </SelectTrigger>
                    <SelectContent>
                      {professionals.map((professional) => (
                        <SelectItem key={professional.id} value={professional.id}>
                          {professional.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Avaliação *</Label>
                  <div className="mt-2">
                    {renderStars(formData.rating, true, (rating) => 
                      setFormData({...formData, rating})
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="comment">Comentário *</Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData({...formData, comment: e.target.value})}
                    placeholder="Conte sobre sua experiência..."
                    rows={4}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full gradient-primary text-white"
                  disabled={submitting}
                >
                  {submitting ? 'Enviando...' : 'Enviar Avaliação'}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  Sua avaliação será analisada antes da publicação.
                </p>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
};

export default Reviews;